// src/components/operation/OperationReport.js
import React, { useState, useEffect } from 'react';
import { Card, Descriptions, Table, Typography, Button, Spin, message, Divider, Tag, Empty, Space } from 'antd';
import { useParams } from 'react-router-dom';
import { 
    CheckCircleOutlined,
    WarningOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import operationsService from '../../services/operationsService';
import { analyticsService } from '../../services/analyticsService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

function OperationReport({ dateOverride }) {
    const { date: routeDate } = useParams(); // Get date from route
    const [operation, setOperation] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [bestSellers, setBestSellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Use dateOverride if provided (for modal), otherwise use route param
    const reportDate = dateOverride || routeDate;

    useEffect(() => {
        if (reportDate) {
            loadReport();
        }
    }, [reportDate]);

    const loadReport = async () => {
        setLoading(true);
        setError(null);
        try {
            // Load operation data
            const opResponse = await operationsService.getOperationReport(reportDate);
            setOperation(opResponse.data);

            // Load analytics for that day
            try {
                const pmResponse = await analyticsService.getPaymentMethods('day');
                setPaymentMethods(pmResponse.data.paymentMethods || []);
            } catch (err) {
                console.log('Payment methods not available:', err);
            }

            try {
                const bsResponse = await analyticsService.getBestSellers(10);
                setBestSellers(bsResponse.data.bestSellers || []);
            } catch (err) {
                console.log('Best sellers not available:', err);
            }
        } catch (error) {
            console.error('Error loading report:', error);
            setError('Error al cargar el informe de operación');
            message.error('Error al cargar informe');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin tip="Cargando informe de operación..." size="large" />
            </div>
        );
    }

    if (error || !operation) {
        return (
            <Card style={{ margin: 24 }}>
                <Empty
                    description={error || 'No se encontró operación para esta fecha'}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                    <Text type="secondary">
                        Fecha solicitada: {dayjs(reportDate).format('DD/MM/YYYY')}
                    </Text>
                </Empty>
            </Card>
        );
    }

    // Calculate totals from movements
    const totalMovements = (operation.movements || []).reduce((sum, m) => sum + Math.abs(m.amount), 0);
    const cashSales = operation.expectedCash - operation.initialCash + totalMovements;

    const getVarianceIcon = (variance) => {
        if (variance === 0) return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        if (Math.abs(variance) < 50) return <WarningOutlined style={{ color: '#faad14' }} />;
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    };

    const getVarianceColor = (variance) => {
        if (variance === 0) return '#52c41a';
        if (variance > 0) return '#1890ff';
        return '#ff4d4f';
    };

    const paymentMethodsColumns = [
        { 
            title: 'Método de Pago', 
            dataIndex: 'name', 
            key: 'name',
            width: '40%'
        },
        { 
            title: 'Código', 
            dataIndex: 'code', 
            key: 'code',
            width: '20%',
            render: (code) => <Tag>{code}</Tag>
        },
        { 
            title: 'Total', 
            dataIndex: 'totalAmount', 
            key: 'totalAmount',
            width: '40%',
            align: 'right',
            render: (val) => <Text strong>${val.toFixed(2)}</Text>
        }
    ];

    const bestSellersColumns = [
        { 
            title: 'Producto', 
            dataIndex: 'productName', 
            key: 'productName',
            width: '50%'
        },
        { 
            title: 'Cantidad', 
            dataIndex: 'totalQuantity', 
            key: 'totalQuantity',
            width: '25%',
            align: 'center'
        },
        { 
            title: 'Total Vendido', 
            dataIndex: 'totalSales', 
            key: 'totalSales',
            width: '25%',
            align: 'right',
            render: (val) => <Text strong>${val.toFixed(2)}</Text>
        }
    ];

    const movementsColumns = [
        { 
            title: 'Hora', 
            dataIndex: 'movementTimestamp',
            key: 'movementTimestamp',
            width: '15%',
            render: (val) => dayjs(val).format('HH:mm:ss')
        },
        { 
            title: 'Tipo', 
            dataIndex: 'movementType',
            key: 'movementType',
            width: '20%',
            render: (type) => {
                const typeMap = {
                    'income': { color: 'green', text: '💰 Pago CC' },
                    'payment': { color: 'cyan', text: 'Pago' },
                    'withdrawal': { color: 'red', text: 'Retiro' },
                    'expense': { color: 'orange', text: 'Gasto' },
                    'adjustment': { color: 'blue', text: 'Ajuste' }
                };
                const tag = typeMap[type] || { color: 'default', text: type };
                return <Tag color={tag.color}>{tag.text}</Tag>;
            }
        },
        { 
            title: 'Monto', 
            key: 'amount',
            width: '20%',
            align: 'right',
            render: (_, record) => {
                const isIncome = record.movementType === 'income' || record.movementType === 'payment';
                const amount = Math.abs(record.amount);
                const sign = isIncome ? '+' : '-';
                const color = isIncome ? '#52c41a' : '#ff4d4f';
                return (
                    <Text strong style={{ color }}>
                        {sign}${amount.toFixed(2)}
                    </Text>
                );
            }
        },
        { 
            title: 'Descripción', 
            dataIndex: 'description',
            key: 'description',
            width: '45%',
            ellipsis: true
        }
    ];

    return (
        <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }} className="operation-report">
            {/* Print Styles */}
            <style>{`
                @media print {
                    .no-print { display: none !important; }
                    .operation-report { padding: 0; }
                }
            `}</style>

            {/* Report Header */}
            <Card style={{ marginBottom: 24 }}>
                <div style={{ textAlign: 'center' }}>
                    <Title level={2}>
                        Informe de Operación Diaria
                    </Title>
                    <Title level={4} type="secondary">
                        {dayjs(reportDate).format('DD/MM/YYYY')}
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        Generado: {dayjs().format('DD/MM/YYYY HH:mm:ss')}
                    </Text>
                </div>
            </Card>

            {/* Operation Summary */}
            <Card title="Resumen de Caja" style={{ marginBottom: 24 }}>
                <Descriptions bordered column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                    <Descriptions.Item label="Apertura">
                        {dayjs(operation.openedAt).format('DD/MM/YYYY HH:mm')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Cierre">
                        {dayjs(operation.closedAt).format('DD/MM/YYYY HH:mm')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Fondo Inicial">
                        <Text strong style={{ fontSize: '16px' }}>
                            ${operation.initialCash.toFixed(2)}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Ventas Efectivo">
                        <Text strong style={{ fontSize: '16px', color: '#52c41a' }}>
                            ${cashSales.toFixed(2)}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Movimientos/Retiros">
                        <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                            ${totalMovements.toFixed(2)}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Esperado">
                        <Text strong style={{ fontSize: '16px', color: '#722ed1' }}>
                            ${operation.expectedCash.toFixed(2)}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Contado">
                        <Text strong style={{ fontSize: '16px' }}>
                            ${operation.countedCash.toFixed(2)}
                        </Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="Descuadre">
                        <Space>
                            {getVarianceIcon(operation.variance)}
                            <Text 
                                strong 
                                style={{ 
                                    fontSize: '18px',
                                    color: getVarianceColor(operation.variance)
                                }}
                            >
                                {operation.variance >= 0 ? '+' : ''}${operation.variance.toFixed(2)}
                            </Text>
                        </Space>
                    </Descriptions.Item>
                </Descriptions>

                {operation.notes && (
                    <>
                        <Divider />
                        <div>
                            <Text strong>Notas:</Text>
                            <br />
                            <Text>{operation.notes}</Text>
                        </div>
                    </>
                )}
            </Card>

            {/* Payment Methods */}
            {paymentMethods.length > 0 && (
                <Card title="Métodos de Pago del Día" style={{ marginBottom: 24 }}>
                    <Table
                        dataSource={paymentMethods}
                        columns={paymentMethodsColumns}
                        pagination={false}
                        size="small"
                        rowKey="code"
                        summary={(pageData) => {
                            const total = pageData.reduce((sum, item) => sum + item.totalAmount, 0);
                            return (
                                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                                    <Table.Summary.Cell colSpan={2}>
                                        <Text strong>Total General</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text strong style={{ fontSize: '16px' }}>
                                            ${total.toFixed(2)}
                                        </Text>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                    />
                </Card>
            )}

            {/* Best Sellers */}
            {bestSellers.length > 0 && (
                <Card title="Productos Más Vendidos" style={{ marginBottom: 24 }}>
                    <Table
                        dataSource={bestSellers}
                        columns={bestSellersColumns}
                        pagination={false}
                        size="small"
                        rowKey="productCode"
                    />
                </Card>
            )}

            {/* Movements Detail */}
            {operation.movements && operation.movements.length > 0 ? (
                <Card title="Detalle de Movimientos">
                    <Table
                        dataSource={operation.movements}
                        columns={movementsColumns}
                        pagination={false}
                        size="small"
                        rowKey="movementId"
                        summary={(pageData) => {
                            const total = pageData.reduce((sum, item) => sum + Math.abs(item.amount), 0);
                            return (
                                <Table.Summary.Row style={{ backgroundColor: '#fff1f0' }}>
                                    <Table.Summary.Cell colSpan={2}>
                                        <Text strong>Total Movimientos</Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell align="right">
                                        <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                                            -${total.toFixed(2)}
                                        </Text>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell />
                                </Table.Summary.Row>
                            );
                        }}
                    />
                </Card>
            ) : (
                <Card title="Detalle de Movimientos">
                    <Empty 
                        description="No hubo movimientos en este día"
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                </Card>
            )}
        </div>
    );
}

export default OperationReport;
